import { useState, useCallback, useRef } from "react";
import { lookupProfile } from "../api/profile";
import type { ProfilePreviewResponse } from "../domain/types";
import { isValidInstagramHandle, normalizeInstagramHandle } from "@lib/utils";
import { t } from "../i18n";

type SearchState =
  | { phase: "idle" }
  | { phase: "validating" }
  | { phase: "loading"; progress: number; steps: string[]; data?: ProfilePreviewResponse }
  | { phase: "success"; data: ProfilePreviewResponse }
  | { phase: "error"; message: string };

export const searchCache: Record<string, ProfilePreviewResponse> = {};

export function useProfileSearch(initialHandle?: string) {
  const [state, setState] = useState<SearchState>(() => {
    if (initialHandle) {
      const normalized = normalizeInstagramHandle(initialHandle);
      if (searchCache[normalized]) {
        return { phase: "success", data: searchCache[normalized] };
      }
    }
    return { phase: "idle" };
  });
  const abortRef = useRef<AbortController | null>(null);

  const search = useCallback(async (rawHandle: string) => {
    // Cancel any in-progress search
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    const normalized = normalizeInstagramHandle(rawHandle);

    if (searchCache[normalized]) {
      setState({ phase: "success", data: searchCache[normalized] });
      return;
    }

    // Validate
    setState({ phase: "validating" });

    if (normalized.length === 0) {
      setState({ phase: "error", message: t("searchErrorEmpty") });
      return;
    }

    if (!isValidInstagramHandle(normalized)) {
      setState({
        phase: "error",
        message: t("searchErrorInvalid"),
      });
      return;
    }

    // Loading with progress simulation
    const steps = [
      "searchLoading1",
      "searchLoading2",
      "searchLoading3",
      "searchLoading4",
      "searchLoading5",
    ];

    let progress = 0;
    let previewData: ProfilePreviewResponse | undefined;

    setState({ phase: "loading", progress, steps });

    const lookupPromise = lookupProfile(normalized)
      .then((data) => {
        previewData = data;
        if (!abortRef.current?.signal.aborted) {
          setState({ phase: "loading", progress, steps, data });
        }
        return { ok: true as const, data };
      })
      .catch(() => ({ ok: false as const }));

    // Simulate progress steps for UX feedback
    for (let i = 0; i < steps.length; i++) {
      if (abortRef.current?.signal.aborted) return;
      await new Promise((r) => setTimeout(r, 600));
      if (abortRef.current?.signal.aborted) return;
      progress = (i + 1) / steps.length;
      setState({ phase: "loading", progress, steps, data: previewData });
    }

    // Actual API call
    const result = await lookupPromise;
    if (!result.ok) {
      if (abortRef.current?.signal.aborted) return;
      setState({
        phase: "error",
        message: t("searchErrorGeneric"),
      });
      return;
    }

    try {
      const { data } = result;

      if (abortRef.current?.signal.aborted) return;

      // Minimum 1.5s loading for UX
      const elapsed = (steps.length) * 600;
      const remaining = 1500 - elapsed;
      if (remaining > 0) {
        await new Promise((r) => setTimeout(r, remaining));
      }

      if (abortRef.current?.signal.aborted) return;

      searchCache[normalized] = data;
      setState({ phase: "success", data });
    } catch {
      if (abortRef.current?.signal.aborted) return;
      setState({
        phase: "error",
        message: t("searchErrorGeneric"),
      });
    }
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setState({ phase: "idle" });
  }, []);

  return { state, search, reset };
}
